import { registerComponents } from './register-components'
import { ChartManager, PieChartService, TableChartService } from "../charts/index.js";
import { ChartService } from '../charts/services/historical-chart.service.js';

import '../newsletters/newsletter.component.js'

// Global Styles
import '../core/styles/webflow.scss'
import { AuthService } from '../auth/services/auth.service.js';
import { OTPService } from '../auth/services/otp.service.js';
import { RouteGuardian } from '../routing/guardian.service.js';
import { NewsService } from '../news/news.service.js';
import { AssetScreenerService } from '../assetScreener/services/assetScreener.service.js';
import { TokenDataService } from '../tokenData/services/tokenData.service.js';
import { PermissionService } from './services/permission-service.js';
import { BubbleChartService } from '../charts/services/bubble.service.js';
import { NumberService } from '../numbers/numbers.service.js';
import axios from 'axios';
import { AuthInterceptor } from './adapters/interceptors/auth.interceptor.js';
import { NewsletterService } from '../newsletters/newsletter.service.js';
import { ProfileService } from '../profile/profile.service.js';
import { ArticleService } from '../content/articles/text/article.service.js';
import { ReportService } from '../content/reports/report.service.js';
import { AnalyticsService } from './services/analytics.service.js';
import { SignupService } from '../auth/services/signup.service.js';

interface TbwServices {
  authService: AuthService;
  signupService: SignupService;
  newsService: NewsService;
  assetScreenerService: AssetScreenerService;
  tokenDataService: TokenDataService;
  chartService: ChartService;
  permissionService: PermissionService;
  bubbleChartService: BubbleChartService;
  pieChartService: PieChartService;
  numberService: NumberService;
  tableChartService: TableChartService;
  newsletterService: NewsletterService;
  profileService: ProfileService;
  articleService: ArticleService;
  reportService: ReportService;
  analyticsService: AnalyticsService;
}

// Extend the Window interface to include authService
declare global {
  interface Window {
    tbwServices: TbwServices;
  }
}

// TODO: minimal compute for guardian actions
const init = async () => {
  const getConfig = async (env: ImportMetaEnv) => Promise.resolve({
    endpoints: {
      api: env.VITE_API_ENDPOINT,
      charts: env.VITE_CHART_API_ENDPOINT,
      search: {
        apiKey: env.VITE_MEILI_CLIENT_KEY,
        url: env.VITE_MEILI_ENDPOINT,
      },
      marketing: env.VITE_MARKETING_WEBSITE,
    }
  });

  const config = await getConfig(import.meta.env);
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }

  const authAdapter = axios.create({
    baseURL: config.endpoints.api + 'sessions',
    headers,
  });
  const otpAdapter = axios.create({
    baseURL: config.endpoints.api + 'sessions',
    headers,
  });
  const chartAdapter = axios.create({
    baseURL: config.endpoints.charts + 'graph',
    headers
  });
  const numberAdapter = axios.create({
    baseURL: config.endpoints.charts + 'widgets',
    headers
  });
  const newsletterAdapter = axios.create({
    baseURL: config.endpoints.api + 'newsletter',
    headers
  });
  const profileAdapter = axios.create({
    baseURL: config.endpoints.api + 'profile',
    headers
  });
  const newsAdapter = axios.create({
    baseURL: config.endpoints.charts + 'articles',
    headers
  });
  const newsV2Adapter = axios.create({
    baseURL: config.endpoints.api + 'news',
    headers
  })
  const contentAdapter = axios.create({
    baseURL: config.endpoints.api,
    headers
  });

  const signupAdapter = axios.create({
    baseURL: config.endpoints.api + 'signup',
    headers,
  });

  const otpService = new OTPService(otpAdapter);
  const authService = new AuthService(authAdapter, otpService);
  const signupService = new SignupService(signupAdapter);
  const authInterceptor = new AuthInterceptor(authService);

  // Add adapters that need AUTH
  [authAdapter, newsV2Adapter, otpAdapter, chartAdapter, numberAdapter, newsAdapter, contentAdapter, newsletterAdapter, profileAdapter].forEach(adapter => {
    authInterceptor.setupInterceptors(adapter);
  });

  const newsService = new NewsService(newsV2Adapter);
  const assetScreenerService = new AssetScreenerService(contentAdapter);
  const tokenDataService = new TokenDataService(contentAdapter);
  const permissionService = new PermissionService(authService);

  const chartService = new ChartService(chartAdapter, numberAdapter);
  const bubbleChartService = new BubbleChartService(chartService);
  const pieChartService = new PieChartService(chartService);
  const tableChartService = new TableChartService(chartService)
  const numberService = new NumberService(numberAdapter);
  const newsletterService = new NewsletterService(newsletterAdapter, authService);
  const profileService = new ProfileService(profileAdapter);
  const articleService = new ArticleService(contentAdapter);
  const reportService = new ReportService(contentAdapter);

  const analyticsService = new AnalyticsService(authService)
  authService.bindAnalytics(analyticsService);

  // required for components services injection
  window.tbwServices = {
    authService,
    signupService,
    newsService,
    assetScreenerService,
    tokenDataService,
    chartService,
    permissionService,
    bubbleChartService,
    pieChartService,
    numberService,
    tableChartService,
    newsletterService,
    profileService,
    articleService,
    reportService,
    analyticsService
  };

  const routeGuardian = new RouteGuardian(authService);
  const canEnterRoute = await routeGuardian.onRouteEntered()
  if (!canEnterRoute) { return }

  registerComponents();

  if (document.querySelector('tbw-world-map')) {
    const { WorldMapComponent } = await import('../world-map/world-map.component')
    customElements.define('tbw-world-map', WorldMapComponent)
  }

  const chartManager = new ChartManager(chartService);
  chartManager.init();
  return true;
}

init()